import type { DowntimeWindow, ScheduleEntry } from './productionTypes';

const MELBOURNE = 'Australia/Melbourne';

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-AU', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: MELBOURNE,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function formatDateHeading(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-AU', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      timeZone: MELBOURNE,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function formatDateKey(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: MELBOURNE,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function formatTimeOfDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: MELBOURNE,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (rem === 0) return `${hours}h`;
  return `${hours}h ${rem}m`;
}

export function formatQuantity(qty: number | null | undefined): string {
  if (qty == null) return '—';
  return new Intl.NumberFormat('en-AU').format(qty);
}

export function addMinutes(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString();
}

/**
 * Return an ISO datetime for the next quarter-hour boundary at or after `from`.
 * Defaults to "now" rounded up to the next 15-minute slot.
 */
export function nextQuarterHour(from: Date = new Date()): string {
  const d = new Date(from);
  d.setSeconds(0, 0);
  const minutes = d.getMinutes();
  const rounded = Math.ceil(minutes / 15) * 15;
  if (rounded === 60) {
    d.setHours(d.getHours() + 1);
    d.setMinutes(0);
  } else {
    d.setMinutes(rounded);
  }
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Downtime / weekend split logic
//
// Given a press's downtime windows and a run's planned_start + total minutes,
// compute the visible segments (running blocks) and the effective end time.
// All wall-clock arithmetic uses the browser's local timezone — production
// runs in Melbourne so this matches the operator's mental model.
// ---------------------------------------------------------------------------

export interface DowntimeInterval {
  start: Date;
  end: Date;
  effect: 'closed' | 'open';
  source: DowntimeWindow;
}

export interface RunSegment {
  index: number;
  start: Date;
  end: Date;
  /** True for every segment after the first (i.e. resumes after a pause). */
  isResume: boolean;
  /** The pause that immediately preceded this segment, if any. */
  pauseBefore?: { start: Date; end: Date };
  /** Minutes of run time consumed in this segment. */
  durationMin: number;
}

export interface SplitResult {
  segments: RunSegment[];
  effectiveEnd: Date;
  /** True if any segment was preceded by a pause. */
  isSplit: boolean;
}

const MS_PER_MIN = 60_000;
const MS_PER_DAY = 86_400_000;

function isoDayOfWeekLocal(d: Date): number {
  const js = d.getDay(); // 0=Sun..6=Sat
  return js === 0 ? 7 : js;
}

function parseTod(hhmm: string | null): number {
  if (!hhmm) return 0;
  // Accept 'HH:MM' or 'HH:MM:SS'.
  const [h = '0', m = '0'] = hhmm.split(':');
  return Number(h) * 60 + Number(m);
}

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * Materialise downtime windows into concrete intervals overlapping [from, to].
 * Recurring windows are expanded across the range; one_off windows pass through.
 * Caller filters by effect afterwards.
 */
export function materialiseDowntimeWindows(
  windows: DowntimeWindow[],
  from: Date,
  to: Date,
): DowntimeInterval[] {
  const out: DowntimeInterval[] = [];
  for (const w of windows) {
    if (w.kind === 'one_off') {
      if (!w.starts_at || !w.ends_at) continue;
      const s = new Date(w.starts_at);
      const e = new Date(w.ends_at);
      if (e > from && s < to) {
        out.push({ start: s, end: e, effect: w.effect, source: w });
      }
      continue;
    }
    // Recurring: weekly pattern. Compute one instance per occurrence of dow_start
    // in the range [from - 8 days, to] so we capture windows that started before
    // `from` but still overlap into it.
    if (w.dow_start == null || w.dow_end == null) continue;
    const dowStart = w.dow_start;
    const todStart = parseTod(w.tod_start);
    const todEnd = parseTod(w.tod_end);
    const dowDelta = (w.dow_end - dowStart + 7) % 7;
    let durationMin = dowDelta * 1440 + (todEnd - todStart);
    if (durationMin <= 0) durationMin += 7 * 1440;

    const cursor = startOfLocalDay(new Date(from.getTime() - 8 * MS_PER_DAY));
    const limit = new Date(to.getTime() + MS_PER_DAY);
    while (cursor <= limit) {
      if (isoDayOfWeekLocal(cursor) === dowStart) {
        const winStart = new Date(cursor);
        winStart.setHours(0, todStart, 0, 0);
        const winEnd = new Date(winStart.getTime() + durationMin * MS_PER_MIN);
        if (winEnd > from && winStart < to) {
          out.push({ start: winStart, end: winEnd, effect: w.effect, source: w });
        }
      }
      // Advance one calendar day (DST-safe).
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  return out;
}

/**
 * Subtract `open` intervals from `closed` intervals, returning the effective
 * closed (= still-blocking) intervals. Used so a one_off OPEN override can
 * carve out a chunk of the recurring weekend band.
 */
function subtractOpen(intervals: DowntimeInterval[]): DowntimeInterval[] {
  const closed = intervals.filter((i) => i.effect === 'closed');
  const opens = intervals
    .filter((i) => i.effect === 'open')
    .sort((a, b) => a.start.getTime() - b.start.getTime());
  if (opens.length === 0) return closed;

  const out: DowntimeInterval[] = [];
  for (const c of closed) {
    let pieces: { s: Date; e: Date }[] = [{ s: c.start, e: c.end }];
    for (const o of opens) {
      const next: typeof pieces = [];
      for (const p of pieces) {
        const overlapStart = p.s > o.start ? p.s : o.start;
        const overlapEnd = p.e < o.end ? p.e : o.end;
        if (overlapStart >= overlapEnd) {
          next.push(p); // no overlap
          continue;
        }
        if (p.s < overlapStart) next.push({ s: p.s, e: overlapStart });
        if (overlapEnd < p.e) next.push({ s: overlapEnd, e: p.e });
      }
      pieces = next;
    }
    for (const p of pieces) {
      out.push({ start: p.s, end: p.e, effect: 'closed', source: c.source });
    }
  }
  return out.sort((a, b) => a.start.getTime() - b.start.getTime());
}

export interface SplitOptions {
  /** When true, ignore downtime entirely — emit one segment of total duration. */
  runThrough?: boolean;
  /** Minutes of fresh setup added to each post-pause segment. Default 0. */
  remakeReadyMinutes?: number;
}

/**
 * Walk a run forward, pausing whenever it hits a closed downtime interval.
 * Returns one segment per running block plus the effective end time.
 */
export function splitRunAroundDowntime(
  startISO: string,
  totalMinutes: number,
  windows: DowntimeWindow[],
  options: SplitOptions = {},
): SplitResult {
  const start = new Date(startISO);
  if (options.runThrough || totalMinutes <= 0 || windows.length === 0) {
    const end = new Date(start.getTime() + Math.max(totalMinutes, 0) * MS_PER_MIN);
    return {
      segments: [
        { index: 0, start, end, isResume: false, durationMin: Math.max(totalMinutes, 0) },
      ],
      effectiveEnd: end,
      isSplit: false,
    };
  }

  // Look-ahead: a 30h run + a 64h weekend = up to ~94h. Pad generously.
  const horizonEnd = new Date(start.getTime() + (totalMinutes + 14 * 1440) * MS_PER_MIN);
  const raw = materialiseDowntimeWindows(windows, start, horizonEnd);
  const effective = subtractOpen(raw);

  const segments: RunSegment[] = [];
  let cursor = start;
  let remaining = totalMinutes;
  let pauseBefore: { start: Date; end: Date } | undefined;

  while (remaining > 0) {
    // Find the next closed interval that begins at or after the cursor.
    const next = effective.find((i) => i.end > cursor && i.start < new Date(cursor.getTime() + remaining * MS_PER_MIN));
    if (!next) {
      const segEnd = new Date(cursor.getTime() + remaining * MS_PER_MIN);
      segments.push({
        index: segments.length,
        start: cursor,
        end: segEnd,
        isResume: pauseBefore !== undefined,
        pauseBefore,
        durationMin: remaining,
      });
      remaining = 0;
      break;
    }

    if (next.start <= cursor) {
      // Cursor sits inside a downtime block — jump to its end.
      cursor = next.end;
      pauseBefore = { start: next.start, end: next.end };
      // Add re-makeready overhead to the next running segment.
      if (options.remakeReadyMinutes && options.remakeReadyMinutes > 0) {
        remaining += options.remakeReadyMinutes;
      }
      continue;
    }

    const runMin = (next.start.getTime() - cursor.getTime()) / MS_PER_MIN;
    if (runMin > 0) {
      segments.push({
        index: segments.length,
        start: cursor,
        end: next.start,
        isResume: pauseBefore !== undefined,
        pauseBefore,
        durationMin: runMin,
      });
      remaining -= runMin;
    }
    cursor = next.end;
    pauseBefore = { start: next.start, end: next.end };
    if (options.remakeReadyMinutes && options.remakeReadyMinutes > 0 && remaining > 0) {
      remaining += options.remakeReadyMinutes;
    }
  }

  const effectiveEnd = segments.length > 0 ? segments[segments.length - 1].end : start;
  return {
    segments,
    effectiveEnd,
    isSplit: segments.length > 1,
  };
}

/**
 * Convenience wrapper for a ScheduleEntry — pulls total minutes and applies
 * the entry's per-row run_through and remakeready flags.
 */
export function splitScheduleEntry(
  entry: ScheduleEntry,
  windows: DowntimeWindow[],
  remakeReadyMinutes: number = 0,
): SplitResult {
  const total =
    Number(entry.planned_setup_min || 0) +
    Number(entry.planned_run_min || 0) +
    Number(entry.planned_washup_min || 0);
  return splitRunAroundDowntime(entry.planned_start, total, windows, {
    runThrough: entry.run_through_downtime ?? false,
    remakeReadyMinutes: entry.needs_remakeready_after_pause ? remakeReadyMinutes : 0,
  });
}
